import { faCalendarAlt, faClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentPet } from '@spike/appointment-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import { useMarketplace } from 'hooks';
import { Moment } from 'moment-timezone';
import React from 'react';
import { PetAvatarInline } from './PetAvatarInline';
import styles from './ReportHeader.module.scss';

interface ReportHeaderProps {
    pet: AppointmentPet;
    petParentName: string;
    petParentPhone?: string;
    reportDate: Moment;
}

const ReportHeaderComponent: React.FC<ReportHeaderProps> = ({ pet, petParentName, petParentPhone, reportDate }) => {
    const marketplace = useMarketplace();

    const formattedPhone = React.useMemo(
        () => (petParentPhone ? formatPhoneNumber(petParentPhone, marketplace.basics.address.country!.id) : undefined),
        [petParentPhone, marketplace.basics.address.country]
    );

    const dateLabel = React.useMemo(() => reportDate.format('dddd, MMMM Do YYYY'), [reportDate]);

    const timeLabel = React.useMemo(() => reportDate.format('h:mm a'), [reportDate]);

    const breedLabel = React.useMemo(
        () => (pet.breed?.name ? `${pet.type.singular ?? ''} - ${pet.breed.name}` : pet.type?.singular ?? ''),
        [pet]
    );

    return (
        <div className={styles.root}>
            <div className={styles.petColumn}>
                <PetAvatarInline pet={pet} />
                <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Pet</span>
                    <span className={styles.infoValue}>{pet.name}</span>
                    {breedLabel && <span className={styles.infoSub}>{breedLabel}</span>}
                </div>
            </div>

            <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Pet Parent</span>
                <span className={styles.infoValue}>{petParentName}</span>
                {formattedPhone && <span className={styles.infoSub}>{formattedPhone}</span>}
            </div>

            <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Report Date</span>
                <span className={styles.dateRow}>
                    <FontAwesomeIcon
                        icon={faClock}
                        className={styles.dateIcon}
                    />
                    <span className={styles.infoValue}>{timeLabel}</span>
                </span>
                <span className={styles.dateRow}>
                    <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className={styles.dateIcon}
                    />
                    <span className={styles.infoSub}>{dateLabel}</span>
                </span>
            </div>
        </div>
    );
};

export const ReportHeader = React.memo(ReportHeaderComponent);
